<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToCenterScroll'
});
</script>
<template>
	<div class="to-centerScroll" :class="setClass" :style="{ height: `${height}em` }">
		<div class="to-centerScroll-top" :style="{ height: `${(height - itemHeight) / 2}em` }"></div>
		<div ref="scrollContainer" class="to-centerScroll-content" @scroll="onScroll">
			<div class="to-centerScroll-wrapper" :style="{ paddingTop: `${(height - itemHeight) / 2}em`, paddingBottom: `${(height - itemHeight) / 2}em` }">
				<div v-for="(item, index) in iData" :key="index" :ref="el => setItemRef(el, index)" class="to-centerScroll-item" :class="{ 'is-on': item.value === modelValue, 'is-dis': item.disabled }">
					<span class="to-centerScroll-item-text">{{ item.label }}</span>
				</div>
			</div>
		</div>
		<div class="to-centerScroll-bottom" :style="{ height: `${(height - itemHeight) / 2}em` }"></div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { centerScrollProps, centerScrollEmits, type CenterScrollDataItem } from './center-scroll'
import { getEm } from '@touchvue/utils'



const props = defineProps(centerScrollProps)
const emit = defineEmits(centerScrollEmits)

// 响应式数据
const scrollContainer = ref<HTMLElement | null>(null)
const itemRefs = ref<(HTMLElement | null)[]>([])
const inited = ref(false)
const iData = ref<CenterScrollDataItem[]>([])
const isScrolling = ref(false)
const scrollTimeout = ref<number | null>(null)
const isScrollTo = ref(false)
const changeBySelf = ref(false)
const itemHeight = ref(0)

// 计算属性
const height = computed(() => Number(props.height))

onMounted(() => {
	itemHeight.value = Number(((itemRefs.value[0].getBoundingClientRect().height || 0) / getEm()).toFixed(2))
})

// 设置item引用
const setItemRef = (el: any, index: number) => {
	if (el) {
		itemRefs.value[index] = el as HTMLElement
	}
}

// 处理数据变化
const handleDataChange = (data: string | unknown[]) => {
	iData.value = []
	if (typeof data === 'string') {
		data.split(',').forEach(item => {
			iData.value.push({
				label: item,
				value: item,
				disabled: false
			})
		})
	} else {
		iData.value = JSON.parse(JSON.stringify(data))
	}
}

// 设置类名
const setClass = computed(() => {
	let arr = []
	if (props.mode) arr.push(`mode-${props.mode}`)
	return arr
})

// 滚动事件处理
const onScroll = () => {
	if (!inited.value) return

	if (scrollTimeout.value) {
		clearTimeout(scrollTimeout.value)
	}

	isScrolling.value = true
	scrollTimeout.value = window.setTimeout(() => {
		isScrolling.value = false
		if (!isScrollTo.value) {
			centerClosestItem()
		}
	}, 200)
}

// 居中最近的项目
const centerClosestItem = () => {
	if (!scrollContainer.value) return

	const container = scrollContainer.value
	const containerRect = container.getBoundingClientRect()
	const containerCenter = containerRect.top + containerRect.height / 2

	let closestItem: CenterScrollDataItem | null = null
	let closestItemEl: HTMLElement | null = null
	let closestDistance = Infinity

	iData.value.forEach((item, index) => {
		if (!item.disabled) {
			const itemEl = itemRefs.value[index]
			if (itemEl) {
				const itemRect = itemEl.getBoundingClientRect()
				const itemCenter = itemRect.top + itemRect.height / 2
				const distance = Math.abs(containerCenter - itemCenter)

				if (distance < closestDistance) {
					closestDistance = distance
					closestItem = item
					closestItemEl = itemEl
				}
			}
		}
	})

	if (closestItemEl && closestItem) {
		scrollTo(closestItemEl)
		changeBySelf.value = true
		emit('update:modelValue', closestItem.value)
		emit('change', closestItem.value)
		nextTick(() => {
			changeBySelf.value = false
		})
	}
}

// 滚动到指定元素
const scrollTo = (el: HTMLElement, behavior: ScrollBehavior = 'smooth') => {
	if (!scrollContainer.value) return

	isScrollTo.value = true
	const itemRect = el.getBoundingClientRect()
	const container = scrollContainer.value
	const containerRect = container.getBoundingClientRect()
	const containerCenter = containerRect.top + containerRect.height / 2
	const offset = itemRect.top + itemRect.height / 2 - containerCenter

	container.scrollTo({
		top: container.scrollTop + offset,
		behavior
	})

	setTimeout(() => {
		isScrollTo.value = false
	}, 500)
}

// 监听modelValue变化
watch(
	() => props.modelValue,
	val => {
		if (changeBySelf.value) return

		const index = iData.value.findIndex(item => item.value === val)
		if (index > -1 && itemRefs.value[index]) {
			scrollTo(itemRefs.value[index]!, 'instant')
		}
	}
)

// 监听data变化
watch(
	() => props.data,
	data => {
		handleDataChange(data as string | CenterScrollDataItem[])
	},
	{ immediate: true }
)

// 组件挂载后初始化
onMounted(() => {
	setTimeout(() => {
		if (props.modelValue) {
			const index = iData.value.findIndex(item => item.value === props.modelValue)
			if (index !== -1 && itemRefs.value[index]) {
				scrollTo(itemRefs.value[index]!, 'instant')
			}
		} else if (iData.value.length > 0) {
			emit('update:modelValue', iData.value[0].value)
			emit('change', iData.value[0].value)
		}

		setTimeout(() => {
			inited.value = true
		}, 100)
	}, 100)
})

// 暴露方法
defineExpose({
	scrollTo,
	centerClosestItem
})
</script>
